/**
 * GOVERNANCE OPERATIONAL VISUAL LANGUAGE V1 — Design Tokens
 *
 * Canonical typed tokens for the operational governance UI.
 *
 * DOCTRINE:
 * These tokens encode infrastructure-tool aesthetics, not SaaS onboarding aesthetics.
 * Design targets: observability tooling, governance instrumentation, replay inspection.
 * Anti-targets: marketing sites, AI assistant UI, consumer productivity apps, wizard flows.
 *
 * INVARIANTS:
 * - Tokens are pure constants. No side effects. No runtime computation.
 * - All px values are numeric (no string units) — rendering layer applies units.
 * - Density modes are discrete states, not continuous gradients.
 * - Color tokens are CSS custom-property names (strings), resolved by the theme layer.
 *   Raw hex values are in colorSemantics.ts.
 * - Spacing scale is a strict geometric progression (base-4), not ad hoc.
 *
 * Schema version: governance-operational-visual-language/v1
 */

export const VISUAL_LANGUAGE_SCHEMA_VERSION =
  'governance-operational-visual-language/v1' as const;

// ---------------------------------------------------------------------------
// Density modes
// ---------------------------------------------------------------------------

/**
 * DensityMode controls row height, padding, and metadata verbosity.
 *
 * - compact: maximum information density, minimum row height.
 *   Used for event logs, trace trees, timeline rows.
 * - standard: balanced density for primary operational reading.
 *   Used for inspector fields, trace headers, continuity pane.
 * - comfortable: reduced density for focused inspection of a single item.
 *   Used for selected inspector detail only — never for list views.
 *
 * Rule: List views and tables MUST use compact or standard.
 *       comfortable is reserved for single-item inspector panels.
 */
export type DensityMode = 'compact' | 'standard' | 'comfortable';

// ---------------------------------------------------------------------------
// Spacing tokens
// ---------------------------------------------------------------------------

/**
 * SpacingScale — base-4 spacing scale in px.
 *
 * Used for: padding, gaps, margins, row heights.
 * Rule: Spacing MUST come from this scale. No arbitrary values.
 * Rule: Outer panel padding MUST NOT exceed sp[4] (16px) except in comfortable inspector.
 */
export const SPACING = {
  sp0: 0,
  sp1: 2,
  sp2: 4,
  sp3: 6,
  sp4: 8,
  sp5: 12,
  sp6: 16,
  sp7: 20,
  sp8: 24,
  sp9: 32,
  sp10: 40,
} as const;

export type SpacingKey = keyof typeof SPACING;

// ---------------------------------------------------------------------------
// Row height tokens
// ---------------------------------------------------------------------------

/**
 * RowHeight — canonical row heights per density mode and row type.
 *
 * All row heights are in px.
 * Rule: Compact list rows MUST NOT exceed ROW_HEIGHT.compact.event.
 * Rule: Timeline rows MUST use ROW_HEIGHT.compact.timeline.
 */
export const ROW_HEIGHT = {
  compact: {
    event: 22,
    timeline: 22,
    trace: 22,
    spanHeader: 26,
    tableRow: 24,
    inspectorField: 20,
  },
  standard: {
    event: 28,
    timeline: 28,
    trace: 28,
    spanHeader: 32,
    tableRow: 30,
    inspectorField: 26,
  },
  comfortable: {
    event: 36,
    timeline: 36,
    trace: 36,
    spanHeader: 40,
    tableRow: 36,
    inspectorField: 32,
  },
} as const;

// ---------------------------------------------------------------------------
// Typography tokens
// ---------------------------------------------------------------------------

/**
 * TypographyScale — font sizes in px.
 *
 * Target: workstation monospace readability at 100% zoom on 1080p+.
 * Rule: Body text in list views MUST NOT exceed typo.body.
 * Rule: Inspector field labels MUST use typo.label or typo.labelSmall.
 * Rule: Timestamps MUST use typo.mono for column alignment.
 * Rule: NO font sizes above typo.heading in any operational pane.
 *       Headings above typo.heading are forbidden in operational context.
 */
export const TYPOGRAPHY = {
  /** Tiny metadata, badge text, chip labels */
  micro: { size: 10, lineHeight: 14, weight: 500 },
  /** Timestamp, hash fragments, trace IDs */
  mono: { size: 11, lineHeight: 16, weight: 400, family: 'monospace' },
  /** Badge/chip labels, status pills */
  label: { size: 11, lineHeight: 16, weight: 600 },
  /** Small metadata labels, inspector keys */
  labelSmall: { size: 10, lineHeight: 14, weight: 500 },
  /** Primary body — event rows, trace labels, inspector values */
  body: { size: 12, lineHeight: 18, weight: 400 },
  /** Slightly emphasized body — span headers, pane section labels */
  bodyMedium: { size: 12, lineHeight: 18, weight: 500 },
  /** Source tag, grouped section titles */
  caption: { size: 11, lineHeight: 16, weight: 400 },
  /** Pane header — only use inside pane title bars */
  heading: { size: 13, lineHeight: 20, weight: 600 },
} as const;

export type TypographyKey = keyof typeof TYPOGRAPHY;

// ---------------------------------------------------------------------------
// Pane dimension tokens
// ---------------------------------------------------------------------------

/**
 * PaneDimensions — canonical widths and minimums in px.
 *
 * Rule: Inspector pane default width is PANE_DIMENSIONS.inspector.defaultWidth.
 * Rule: Timeline pane MUST NOT have a minimum narrower than PANE_DIMENSIONS.timeline.minWidth.
 * Rule: Pane resize handles use PANE_DIMENSIONS.resizeHandleWidth.
 */
export const PANE_DIMENSIONS = {
  timeline: {
    minWidth: 260,
    defaultWidth: 320,
    laneWidth: 8,
    laneSeparator: 1,
  },
  traceExplorer: {
    minWidth: 280,
    defaultWidth: 360,
    indentPerDepth: 14,
  },
  inspector: {
    minWidth: 280,
    defaultWidth: 360,
    maxWidth: 520,
    labelColumnWidth: 120,
    valueColumnMinWidth: 140,
  },
  eventLog: {
    minWidth: 220,
    defaultWidth: 400,
    seqColumnWidth: 40,
    timestampColumnWidth: 90,
    sourceTagColumnWidth: 80,
    severityColumnWidth: 56,
    labelMinWidth: 120,
  },
  continuity: {
    minWidth: 200,
    defaultWidth: 280,
    markerWidth: 10,
    transitionRowHeight: 26,
  },
  admissibilityChain: {
    minWidth: 240,
    defaultWidth: 340,
    indentPerDepth: 12,
  },
  replayCompare: {
    minWidth: 240,
    defaultWidth: 340,
  },
  ghaTemporalPane: {
    minWidth: 260,
    defaultWidth: 360,
    runIdColumnWidth: 80,
    workflowColumnWidth: 140,
    conclusionColumnWidth: 64,
  },
  resizeHandleWidth: 4,
  separatorThickness: 1,
} as const;

// ---------------------------------------------------------------------------
// Collapse thresholds
// ---------------------------------------------------------------------------

/**
 * CollapseThreshold — minimum pane width (px) before auto-collapse.
 *
 * Rule: Panes narrower than their collapseThreshold collapse to icon strip only.
 */
export const COLLAPSE_THRESHOLD = {
  timeline: 200,
  traceExplorer: 220,
  inspector: 240,
  eventLog: 180,
  continuity: 160,
  admissibilityChain: 200,
  replayCompare: 200,
  ghaTemporalPane: 200,
} as const;

// ---------------------------------------------------------------------------
// Timeline lane constants
// ---------------------------------------------------------------------------

/**
 * TimelineLane — visual constants for timeline lane rendering.
 *
 * Lanes correspond to event sources / hierarchy levels.
 * Rule: GHA hierarchy (workflow → job → step) uses nested lane indentation.
 * Rule: Lane separator is ALWAYS 1px, never thicker.
 */
export const TIMELINE_LANE = {
  separator: 1,
  markerDiameter: 6,
  markerBorderWidth: 1,
  connectorWidth: 1,
  continuityMarkerWidth: 3,
  refusalOverlayWidth: 2,
  replayWindowOverlayOpacity: 0.12,
  /** GHA nesting: workflow=0, job=1, step=2 */
  ghaDepthIndent: 10,
  spanBracketWidth: 2,
  spanBracketOpacity: 0.5,
} as const;

// ---------------------------------------------------------------------------
// Inspector rendering constants
// ---------------------------------------------------------------------------

/**
 * InspectorConstants — rendering rules for inspector panels.
 *
 * Rule: Default state is compact metadata — NOT narrative text.
 * Rule: Hash fragments shown at max INSPECTOR.hashDisplayLength chars.
 * Rule: Max inspector depth for nested objects is INSPECTOR.maxNestingDepth.
 * Rule: proofOnly banner MUST be visible when proofOnly=true.
 */
export const INSPECTOR = {
  hashDisplayLength: 16,
  maxNestingDepth: 3,
  maxValueLineLength: 80,
  /** Chars of a long value shown before truncation */
  truncateValueAt: 64,
  /** Number of safeMetadata rows visible before "show more" */
  initialMetadataRows: 6,
  proofOnlyBannerHeight: 24,
} as const;

// ---------------------------------------------------------------------------
// Table/list compression constants
// ---------------------------------------------------------------------------

/**
 * TableCompression — canonical row and cell rules for dense operational tables.
 *
 * Rule: Table row padding uses SPACING.sp2 (4px) vertical.
 * Rule: First column (seq/timestamp) is right-aligned monospace.
 * Rule: Severity column is fixed width; NEVER wraps.
 * Rule: Label column truncates with ellipsis at maxLabelChars.
 */
export const TABLE_COMPRESSION = {
  rowPaddingV: SPACING.sp2,
  rowPaddingH: SPACING.sp3,
  maxLabelChars: 80,
  maxSourceTagChars: 12,
  maxEventTypeChars: 32,
  timestampFormat: 'HH:mm:ss.SSS',
  fullTimestampFormat: 'YYYY-MM-DDTHH:mm:ss.SSSZ',
  /** When true, show only time portion in timestamp column (saves width) */
  compactTimestamps: true,
} as const;

// ---------------------------------------------------------------------------
// Badge/chip rendering constants
// ---------------------------------------------------------------------------

/**
 * ChipConstants — rendering rules for severity chips, source tags, status pills.
 *
 * Rule: Chips are always single-line. No multi-line chips.
 * Rule: Chip text uses TYPOGRAPHY.label.
 * Rule: Chip padding is horizontal SPACING.sp2 (4px), vertical SPACING.sp1 (2px).
 * Rule: Max chip display text is CHIP.maxTextChars characters.
 */
export const CHIP = {
  paddingH: SPACING.sp2,
  paddingV: SPACING.sp1,
  borderRadius: 3,
  maxTextChars: 12,
  fontSize: TYPOGRAPHY.label.size,
  fontWeight: TYPOGRAPHY.label.weight,
} as const;

// ---------------------------------------------------------------------------
// Separator tokens
// ---------------------------------------------------------------------------

/**
 * Separator — canonical separator/divider rules.
 *
 * Rule: All separators are 1px. Never use 2px+ separators in operational panes.
 * Rule: Horizontal separators are only used between major pane sections, not between rows.
 * Rule: Row separators are transparent — use background alternation only in tables.
 */
export const SEPARATOR = {
  thickness: 1,
  /** CSS variable name for separator color — resolved by theme */
  colorVar: '--op-separator',
  /** CSS variable name for subtle row alternation */
  rowAlternationVar: '--op-row-alt',
} as const;

// ---------------------------------------------------------------------------
// Animation tokens
// ---------------------------------------------------------------------------

/**
 * Animation — constrained animation rules.
 *
 * Rule: Operational panes do NOT animate layout changes.
 * Rule: Selection highlight transitions use ANIMATION.selectionMs only.
 * Rule: NO spring animations, NO elastic easing, NO decorative motion.
 */
export const ANIMATION = {
  selectionMs: 80,
  collapseMs: 120,
  /** Easing for all operational transitions */
  easing: 'ease-out',
} as const;
